import { useCallback, useEffect, useRef, useState } from 'react';
import {
	ArrowUpTrayIcon,
	CheckCircleIcon,
	ExclamationCircleIcon,
	XCircleIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { Button, Modal } from '../ui';
import CsvUploadZone from './CsvUploadZone';
import { customersApi, ApiError } from '../../api';
import { downloadCsvString, validateCustomerCsvFile } from '../../utils/csvParser';
import type { CustomerImportJob } from '../../types/customer';
import type { BulkUploadStatus } from '../../types/provider';

type CsvStatusType = 'info' | 'success' | 'error' | 'warning' | null;

const POLL_INTERVAL_MS = 3000;
const TERMINAL_STATUSES: BulkUploadStatus[] = ['COMPLETED', 'FAILED', 'PARTIALLY_COMPLETED'];

const FORMAT_GUIDE = [
	'Required columns: Name, Email, Phone. Every row must have all three.',
	'Optional column: DOB, as YYYY-MM-DD (MM/DD/YYYY is accepted too).',
	'Example row: Jane Doe, jane@example.com, (555) 123-4567, 1990-05-14',
	'Customers who already exist (same email or phone) are added to this location, not duplicated.',
	'Up to 2,000 customers per file.',
];

interface CustomerImportModalProps {
	isOpen: boolean;
	locationId: number;
	onClose: () => void;
	/** Called once the import reaches a final state so the list can refresh. */
	onImported: () => void;
}

const CustomerImportModal = ({
	isOpen,
	locationId,
	onClose,
	onImported,
}: CustomerImportModalProps) => {
	const [csvFile, setCsvFile] = useState<File | null>(null);
	const [fileInfo, setFileInfo] = useState<{ totalRows: number; incompleteRows: number } | null>(
		null
	);
	const [statusMessage, setStatusMessage] = useState<string | null>(null);
	const [statusType, setStatusType] = useState<CsvStatusType>(null);
	const [isUploading, setIsUploading] = useState(false);
	const [isDownloadingTemplate, setIsDownloadingTemplate] = useState(false);

	const [jobStatus, setJobStatus] = useState<BulkUploadStatus | null>(null);
	const [job, setJob] = useState<CustomerImportJob | null>(null);
	const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

	const stopPolling = useCallback(() => {
		if (pollingRef.current) {
			clearInterval(pollingRef.current);
			pollingRef.current = null;
		}
	}, []);

	const reset = useCallback(() => {
		stopPolling();
		setCsvFile(null);
		setFileInfo(null);
		setStatusMessage(null);
		setStatusType(null);
		setIsUploading(false);
		setJobStatus(null);
		setJob(null);
	}, [stopPolling]);

	useEffect(() => {
		if (isOpen) reset();
	}, [isOpen, reset]);

	useEffect(() => stopPolling, [stopPolling]);

	const handleClose = () => {
		// The job keeps running on the server; closing only stops watching it.
		stopPolling();
		onClose();
	};

	const handleDownloadTemplate = useCallback(async () => {
		setIsDownloadingTemplate(true);
		try {
			const csv = await customersApi.downloadImportTemplate();
			downloadCsvString(csv, 'customer_import_template.csv');
		} catch (e) {
			toast.error(e instanceof ApiError ? e.message : 'Failed to download template');
		} finally {
			setIsDownloadingTemplate(false);
		}
	}, []);

	const handleFile = useCallback(async (file: File) => {
		setStatusMessage('Checking file...');
		setStatusType('info');
		const result = await validateCustomerCsvFile(file);
		if (!result.valid) {
			setCsvFile(null);
			setFileInfo(null);
			setStatusMessage(result.error ?? 'Invalid CSV file');
			setStatusType('error');
			return;
		}
		setCsvFile(file);
		setFileInfo({ totalRows: result.totalRows, incompleteRows: result.incompleteRows });
		if (result.incompleteRows > 0) {
			setStatusMessage(
				`${result.totalRows} row(s) found. ${result.incompleteRows} row(s) are missing a name, email or phone and will be rejected.`
			);
			setStatusType('warning');
		} else {
			setStatusMessage(`${result.totalRows} row(s) ready to import.`);
			setStatusType('success');
		}
	}, []);

	const startPolling = useCallback(
		(jobId: number) => {
			stopPolling();
			pollingRef.current = setInterval(async () => {
				try {
					const result = await customersApi.getImportStatus(jobId);
					setJob(result);
					setJobStatus(result.status);
					if (TERMINAL_STATUSES.includes(result.status)) {
						stopPolling();
						onImported();
					}
				} catch {
					stopPolling();
					setJobStatus('FAILED');
					toast.error('Failed to check import status');
				}
			}, POLL_INTERVAL_MS);
		},
		[stopPolling, onImported]
	);

	const handleImport = async () => {
		if (!csvFile || isUploading) return;
		setIsUploading(true);
		try {
			const started = await customersApi.importCsv(locationId, csvFile);
			setJob(started);
			setJobStatus(started.status);
			if (TERMINAL_STATUSES.includes(started.status)) {
				onImported();
			} else {
				startPolling(started.id);
			}
		} catch (e) {
			toast.error(e instanceof ApiError ? e.message : 'Import failed');
		} finally {
			setIsUploading(false);
		}
	};

	const created = job ? job.successRows - (job.linkedRows ?? 0) : 0;
	const linked = job?.linkedRows ?? 0;

	const renderErrors = () =>
		job?.errors && job.errors.length > 0 ? (
			<div className="bulk-upload-status__errors max-h-60 overflow-y-auto">
				<h4>Rows not imported:</h4>
				<ul>
					{job.errors.map((err, i) => (
						<li key={i}>
							Row {err.rowIndex}: {err.errorMessage}
						</li>
					))}
				</ul>
			</div>
		) : null;

	const summary = job
		? `${created} customer(s) created, ${linked} existing customer(s) linked, ${job.failedRows} row(s) failed.`
		: '';

	return (
		<Modal isOpen={isOpen} onClose={handleClose} title="Import Customers" size="lg">
			{jobStatus ? (
				<div className="bulk-upload-status">
					{(jobStatus === 'PENDING' || jobStatus === 'PROCESSING') && (
						<div className="bulk-upload-status__card bulk-upload-status__card--processing">
							<div className="bulk-upload-status__spinner" />
							<h3 className="bulk-upload-status__title">
								{jobStatus === 'PENDING' ? 'Import Queued' : 'Importing Customers'}
							</h3>
							<p className="bulk-upload-status__message">
								Each new customer gets an account, so this can take a moment. You
								can close this window; the import keeps running.
							</p>
							{job && job.totalRows > 0 && (
								<p className="bulk-upload-status__progress">
									{job.processedRows} of {job.totalRows} rows processed
								</p>
							)}
						</div>
					)}

					{jobStatus === 'COMPLETED' && job && (
						<div className="bulk-upload-status__card bulk-upload-status__card--success">
							<CheckCircleIcon className="bulk-upload-status__icon bulk-upload-status__icon--success" />
							<h3 className="bulk-upload-status__title">Import Complete</h3>
							<p className="bulk-upload-status__message">{summary}</p>
							<Button variant="primary" onClick={handleClose} className="mt-4">
								View Customers
							</Button>
						</div>
					)}

					{jobStatus === 'PARTIALLY_COMPLETED' && job && (
						<div className="bulk-upload-status__card bulk-upload-status__card--warning">
							<ExclamationCircleIcon className="bulk-upload-status__icon bulk-upload-status__icon--warning" />
							<h3 className="bulk-upload-status__title">Partially Imported</h3>
							<p className="bulk-upload-status__message">{summary}</p>
							{renderErrors()}
							<Button variant="primary" onClick={handleClose} className="mt-4">
								View Customers
							</Button>
						</div>
					)}

					{jobStatus === 'FAILED' && (
						<div className="bulk-upload-status__card bulk-upload-status__card--error">
							<XCircleIcon className="bulk-upload-status__icon bulk-upload-status__icon--error" />
							<h3 className="bulk-upload-status__title">Import Failed</h3>
							<p className="bulk-upload-status__message">
								{job
									? summary
									: 'The import could not be completed. Please check your CSV file and try again.'}
							</p>
							{renderErrors()}
							<Button variant="secondary" onClick={reset} className="mt-4">
								Try Another File
							</Button>
						</div>
					)}
				</div>
			) : (
				<>
					<CsvUploadZone
						label="Customer CSV"
						description="Import your customer list for this location. Name, Email and Phone are required for every customer."
						onFile={handleFile}
						onDownloadTemplate={handleDownloadTemplate}
						isDownloading={isDownloadingTemplate}
						statusMessage={statusMessage}
						statusType={statusType}
						formatGuideItems={FORMAT_GUIDE}
					/>

					{csvFile && fileInfo && (
						<div className="csv-upload__file-info">
							<div className="csv-upload__file-info-details">
								<p className="csv-upload__file-info-name">{csvFile.name}</p>
								<p className="csv-upload__file-info-meta">
									{fileInfo.totalRows} row(s)
									{fileInfo.incompleteRows > 0 &&
										` · ${fileInfo.incompleteRows} incomplete`}
								</p>
							</div>
							<Button
								variant="primary"
								onClick={handleImport}
								isLoading={isUploading}
							>
								<ArrowUpTrayIcon className="w-4 h-4 mr-1" />
								Import Customers
							</Button>
						</div>
					)}
				</>
			)}
		</Modal>
	);
};

export default CustomerImportModal;
